import NiceModal from '@ebay/nice-modal-react';
import { faCog } from '@fortawesome/free-solid-svg-icons/faCog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Box, Chip, Divider, Drawer, Menu, MenuItem, Toolbar } from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useAuth } from '@turiya/login-component';
import { useRendererContext } from '@turiya/orchestrator-renderer-react';
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import React, { Suspense, useEffect } from 'react';
import { NotificationManager } from 'react-notifications';
import { Location, Route, Routes, useLocation } from 'react-router-dom';
import logo from '../../assets/phoenix.png';
import { API_WORKFLOW_KEY, DynamicEnvironment } from '../../common/env';
import { GET_ENVIRONMENT_CONFIGURATIONS_MESSAGE } from '../../common/model';
import { ROUTES_TO_SCREEN, SCREEN_ROUTES, Screen } from '../../common/screens';
import { mainMenuItems } from '../../common/sidebarItems';
import { getEnvironment, useDataState, useLoaderState } from '../../common/state';
import { DoubleChevron } from '../../components/DoubleChevron';
import { SideBarGroup } from '../../components/SideBar';
import { LOGIN_METHOD_LS_KEY, SSO_PROVIDER_LS_KEY } from '../../components/auth/LoginScreen';
import { useClientId } from '../../components/auth/useClientid';
import { isUserAdmin } from '../../components/auth/utils';
import './App.css';
import { SFTPModal } from './SFTPModal';
import { GlobalSearch } from '../globalSearch/GlobalSearch';
import './loader.css';

const drawerWidth = 240;

const Dashboard = React.lazy(() => import('../dashboard/Dashboard'));
const Operations = React.lazy(() => import('../operations/Operations'));
const ManualTasksV2 = React.lazy(() => import('../manualTasksV2/ManualTasksV2'));
const GenerateReport = React.lazy(() => import('../generateReport/GenerateReport'));
const UserManagement = React.lazy(() => import('../users/AccessManagement'));

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
}>(({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
    }),
}));

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

export const getCurrentScreen = (location: Location): Screen => ROUTES_TO_SCREEN[location.pathname];

export const extractUserName = (preferredUserName: string, appId: string): string => {
    const prefix = `extuser_wf_${appId}_${API_WORKFLOW_KEY}_`;
    if (preferredUserName.startsWith(prefix)) {
        return decodeURIComponent(preferredUserName.slice(prefix.length));
    }
    return preferredUserName;
};

export const App: React.FC = () => {
    const { signOut, getUserInfo, getCurrentToken } = useAuth();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const loadingAnimationRequests = useLoaderState((state) => state.requests);
    const { toggleDrawer, open, toggleMetricsView, setDynamicEnvironments } = useDataState();
    const { enableSSO, enableSSOAutomaticLogin, appId } = useDataState(getEnvironment);

    const closeUserMenu = (): void => setAnchorEl(null);
    const handleLogout = async (): Promise<void> => signOut().finally(closeUserMenu);
    const handleToggleMetricView = (): void => {
        toggleMetricsView();
        closeUserMenu();
    };
    // const handleManualTaskDetailedView = (): void => {
    //     toggleManualtaskDetailedView();
    //     closeUserMenu();
    // };
    const openUserMenu = (event: React.MouseEvent<HTMLElement>): void => setAnchorEl(event.currentTarget);

    const location = useLocation();
    const screen = getCurrentScreen(location);
    const isManualTask = screen === Screen.ManualTaskV2;
    const isAdmin = isUserAdmin(getCurrentToken() ?? '');

    const getEngineApi = useRendererContext().getEngineApi;
    const { clientId } = useClientId();

    useEffect(() => {
        const getDynamicEnvironmentConfigurations = async (): Promise<void> => {
            if (clientId)
                getEngineApi?.()
                    ?.startWithMessage(GET_ENVIRONMENT_CONFIGURATIONS_MESSAGE, {
                        clientName: clientId,
                    })
                    .then((r) =>
                        setDynamicEnvironments(r.variables.dynamicEnvironmentConfigurations as DynamicEnvironment),
                    )
                    .catch(() => {
                        NotificationManager.error(
                            `There was an error trying to get application configurations, please refresh the application to try again`,
                        );
                    });
        };
        getDynamicEnvironmentConfigurations();
    }, [getEngineApi, setDynamicEnvironments, clientId]);

    return (
        <Box sx={{ display: 'flex', backgroundColor: isManualTask ? '#ffffff' : '#F4F6FB' }} flex={1}>
            <AppBar position={'fixed'} open={open} elevation={1}>
                <Toolbar sx={{ background: 'white' }}>
                    <IconButton
                        sx={{ mr: 2 }}
                        edge={'start'}
                        color={'default'}
                        onClick={toggleDrawer}
                        aria-label={'open drawer'}>
                        <DoubleChevron direction={open ? 'left' : 'right'} />
                    </IconButton>
                    <Typography variant={'h6'} noWrap component={'div'} color={'black'} sx={{ flexGrow: 1 }}>
                        {isManualTask ? Screen.ManualTaskV2 : screen}
                    </Typography>
                    {loadingAnimationRequests > 0 ? (
                        <div style={{ marginRight: '1em' }}>
                            <FontAwesomeIcon
                                spin
                                icon={faCog}
                                data-testid={'header-cog'}
                                style={{ color: '#373a47', fontSize: '1.6em', float: 'right' }}
                            />
                        </div>
                    ) : null}
                    <div>
                        <IconButton
                            size={'medium'}
                            color={'default'}
                            aria-haspopup={'true'}
                            onClick={openUserMenu}
                            aria-controls={'menu-appbar'}>
                            <Avatar sx={{ width: '1.6em', height: '1.6em' }} />
                            <Typography style={{ marginLeft: '10px' }}>
                                {getUserInfo()?.name ?? extractUserName(getUserInfo()?.preferred_username ?? '', appId)}
                            </Typography>
                            {isAdmin && (
                                <Chip
                                    variant={'outlined'}
                                    color={'error'}
                                    label={'Admin'}
                                    size={'small'}
                                    sx={{ marginLeft: '5px' }}
                                />
                            )}
                        </IconButton>
                        <Menu
                            keepMounted
                            id={'menu-appbar'}
                            anchorEl={anchorEl}
                            onClose={closeUserMenu}
                            open={Boolean(anchorEl)}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                            {enableSSO && enableSSOAutomaticLogin && localStorage.getItem(LOGIN_METHOD_LS_KEY) ? (
                                <MenuItem
                                    onClick={(): void => {
                                        localStorage.removeItem(LOGIN_METHOD_LS_KEY);
                                        localStorage.removeItem(SSO_PROVIDER_LS_KEY);
                                        closeUserMenu();
                                    }}>
                                    Reset Automatic Login
                                </MenuItem>
                            ) : null}
                            {isAdmin ? (
                                <MenuItem
                                    onClick={(): Promise<void> => {
                                        return NiceModal.show(SFTPModal);
                                    }}>
                                    Reset SFTP Credentials
                                </MenuItem>
                            ) : null}
                            <MenuItem onClick={handleToggleMetricView}>Toggle Metrics</MenuItem>
                            {/* <MenuItem onClick={handleManualTaskDetailedView}>Toggle Manual Task Breakdown V2</MenuItem> */}
                            <MenuItem onClick={handleLogout}>Logout</MenuItem>
                        </Menu>
                    </div>
                </Toolbar>
            </AppBar>
            <Drawer
                open={open}
                anchor={'left'}
                variant={'persistent'}
                sx={{
                    display: 'flex',
                    width: drawerWidth,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        background: '#081A51',
                        boxSizing: 'border-box',
                    },
                }}>
                <DrawerHeader style={{ justifyContent: 'center', flexDirection: 'column' }}>
                    <img
                        alt={'logo'}
                        src={logo}
                        width={'auto'}
                        height={'65px'}
                        style={{ paddingLeft: '20px', paddingRight: '20px', paddingTop: '20px', paddingBottom: '10px' }}
                    />
                    <Typography sx={{ color: '#FFFFFF', textAlign: 'center', fontSize: 24 }}>Phoenix</Typography>
                </DrawerHeader>
                <SideBarGroup
                    header={'Main Menu'}
                    items={mainMenuItems.filter((item) =>
                        !isAdmin && item.screen === Screen.UserAccess ? false : true,
                    )}
                />
                <div style={{ flexGrow: 1 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                    <Typography sx={{ color: '#FFFFFF', py: '10px', px: '10px', textAlign: 'center', fontSize: 13 }}>
                        Made in India 🇮🇳 with ❤️ by Shibui
                    </Typography>
                    <Divider color={'#FFF'} sx={{ width: '50%' }} />
                    <Typography sx={{ color: '#FFFFFF', py: '10px', px: '10px', textAlign: 'center', fontSize: 12 }}>
                        © 2023 Shibui Technologies Private Limited.
                    </Typography>
                </div>
            </Drawer>
            <Main
                open={open}
                sx={{
                    display: 'flex',
                    flex: 1,
                    marginTop: isManualTask ? '3em' : '4em',
                    paddingLeft: isManualTask ? '0px !important' : null,
                    paddingRight: isManualTask ? '0px !important' : null,
                }}>
                <Suspense fallback={<div>Loading...</div>}>
                    <Routes>
                        <Route path={SCREEN_ROUTES[Screen.Dashboard]} element={<Dashboard />} />
                        <Route path={SCREEN_ROUTES[Screen.Operations]} element={<Operations />} />
                        <Route path={SCREEN_ROUTES[Screen.ManualTaskV2]} element={<ManualTasksV2 />} />
                        <Route path={SCREEN_ROUTES[Screen.GenerateReport]} element={<GenerateReport />} />
                        <Route path={SCREEN_ROUTES[Screen.GlobalSearch]} element={<GlobalSearch />} />
                        {isAdmin ? <Route path={SCREEN_ROUTES[Screen.UserAccess]} element={<UserManagement />} /> : null}
                    </Routes>
                </Suspense>
            </Main>
        </Box>
    );
};
